import * as yup from 'yup';
import dayjs from 'dayjs';
import { currencies } from '@/utils/currenciesUtils';
import {
  ASSET_FIELDS,
  ASSET_KEYS,
  AssetKey,
  EXPENSE_KEYS,
  ExpenseKey,
  HISTORY_YEARS,
  MAX_PREVIOUS_ENTRIES,
} from './constant';

const REQUIRED = 'This field is required.';
export const EARLIEST_DATE = dayjs('1900-01-01');
const MAX_MONTHLY_EXPENSE = 10_000_000;
const MAX_ASSET_VALUE = 1_000_000_000;

// A typed 0 (not a blank): Medical and Health then needs a reason.
export const isExplicitZero = (v?: string | null) =>
  v !== undefined && v !== null && v.trim() !== '' && Number(v) === 0;
const hasLetter = (v?: string) => !v || /\p{L}/u.test(v);
const hasDate = (v?: string | null) => !!v && dayjs(v).isValid();
const toDate = (v?: string | null) => (hasDate(v) ? dayjs(v) : null);
const fmt = (v?: string | null) => toDate(v)?.format('DD/MM/YYYY') ?? '';

/**
 * How many previous entries (addresses / jobs) a history needs to reach back
 * HISTORY_YEARS.
 * Walks current -> previous start dates; stops at the first one old enough,
 * or at a blank one (can't tell yet). All filled and still short -> one more.
 */
/**
 * What the history blocks should do next: open one more while the history is
 * short, close the last one when it isn't needed and is an untouched block
 * the form opened itself, otherwise nothing.
 */
export const historyBlockAction = (
  count: number,
  needed: number,
  lastIsAutoOpenedAndBlank: boolean,
): 'append' | 'remove' | null => {
  if (count < needed) return 'append';
  if (count > needed && lastIsAutoOpenedAndBlank) return 'remove';
  return null;
};

export const previousEntriesNeeded = (
  currentStart?: string | null,
  previous: { startDate?: string | null }[] = [],
) => {
  const cutoff = dayjs().subtract(HISTORY_YEARS, 'year');
  const starts = [currentStart, ...previous.map((p) => p?.startDate)];
  for (let i = 0; i < starts.length; i++) {
    const s = toDate(starts[i]);
    if (!s || !s.isAfter(cutoff, 'day')) return i;
  }
  return starts.length;
};

/**
 * The purchase application stores the current job's start as month + year
 * ("february", "2017"); the fact-find wants a date, so use the 1st of that
 * month in DatePickerField's stored format. '' when either part is missing.
 */
const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];
export const jobStartDate = (month?: string, year?: string) => {
  const m = MONTHS.indexOf(String(month ?? '').toLowerCase());
  const y = Number(year);
  if (m < 0 || !Number.isInteger(y) || y < 1900) return '';
  const d = dayjs(new Date(y, m, 1));
  return d.isAfter(dayjs())
    ? ''
    : `${d.format('YYYY-MM-DD')} 00:00:00.000000+00`;
};

const name = (required: boolean) => {
  const s = yup
    .string()
    .trim()
    .max(50, 'Must be 50 characters or fewer.')
    // Digits allowed: some legal names contain them (e.g. "X Æ A-12").
    .matches(/^[\p{L}\p{M}\p{N}' .-]*$/u, {
      message:
        "Use letters, numbers, spaces, hyphens (-) and apostrophes (') only.",
      excludeEmptyString: true,
    })
    .test('has-letter', 'Must include at least one letter.', hasLetter);
  return required ? s.required(REQUIRED) : s.optional();
};

/**
 * Addresses are stored as objects with snake_case keys so they reach the
 * backend unchanged (the key mapper is shallow). Picking a Google suggestion
 * fills every part Google knows; typing clears them, so an empty `country`
 * means the address wasn't picked from Google and is rejected.
 * Parts a country doesn't have (suburb, postcode...) stay ''.
 */
export const EMPTY_ADDRESS = {
  formatted_address: '',
  unit: '',
  street_number: '',
  street_name: '',
  suburb: '',
  city: '',
  state: '',
  postcode: '',
  country: '',
};
export type Address = typeof EMPTY_ADDRESS;
export const ADDRESS_PARTS = (
  Object.keys(EMPTY_ADDRESS) as (keyof Address)[]
).filter((k) => k !== 'formatted_address');

/** GoogleCustomLocationField place details -> stored address. */
export const toAddress = (d: Record<string, string | undefined>): Address => {
  // In Australia Google's `locality` is the suburb and there is no city.
  const au = d.countryCode === 'AU';
  return {
    formatted_address: d.fullAddress ?? '',
    unit: d.apartment ?? '',
    street_number: d.streetNumber ?? '',
    street_name: d.street ?? '',
    suburb: (au ? d.suburb : d.sublocality) ?? '',
    city: (au ? '' : d.city) ?? '',
    state: d.state ?? '',
    postcode: d.postcode ?? '',
    country: d.country ?? '',
  };
};

const addressPart = () => yup.string().default('');

const address = (required: boolean) => {
  const text = yup
    .string()
    .trim()
    .max(255, 'Must be 255 characters or fewer.')
    .test(
      'min-length',
      'Please enter the full address.',
      (v) => !v || v.length >= 5,
    )
    .test(
      'has-letter',
      'Address must include a street or city name.',
      hasLetter,
    )
    .test(
      'from-google',
      'Please select your address from the suggestions.',
      function (v) {
        return !v || !!this.parent.country;
      },
    );
  return yup.object({
    formatted_address: required ? text.required(REQUIRED) : text.optional(),
    unit: addressPart(),
    street_number: addressPart(),
    street_name: addressPart(),
    suburb: addressPart(),
    city: addressPart(),
    state: addressPart(),
    postcode: addressPart(),
    country: addressPart(),
  });
};

const shortText = (required: boolean) => {
  const s = yup
    .string()
    .trim()
    .max(100, 'Must be 100 characters or fewer.')
    .test(
      'has-letter',
      'Must include letters, not just numbers or symbols.',
      hasLetter,
    );
  return required ? s.required(REQUIRED) : s.optional();
};

const longText = (max: number) =>
  yup.string().trim().max(max, `Must be ${max} characters or fewer.`);

/** A past (or today) calendar date stored as the DatePickerField string. */
const pastDate = (required: boolean, requiredMsg = REQUIRED) => {
  const s = yup
    .string()
    .nullable()
    .test('valid', 'Enter a valid date.', (v) => !v || hasDate(v))
    .test(
      'not-future',
      'Date cannot be in the future.',
      (v) => !hasDate(v) || !dayjs(v).isAfter(dayjs(), 'day'),
    )
    .test(
      'not-too-early',
      'Date must be after 01/01/1900.',
      (v) => !hasDate(v) || !dayjs(v).isBefore(EARLIEST_DATE, 'day'),
    );
  return required ? s.required(requiredMsg) : s.optional();
};

/** Non-negative amount with at most 2 decimals, entered as a string. */
const amount = (
  required: boolean,
  max: number,
  requiredMsg = 'Enter an amount (enter 0 if none).',
) => {
  const s = yup
    .string()
    .trim()
    .test(
      'number',
      'Enter a valid amount (numbers only, up to 2 decimals).',
      (v) => !v || /^\d+(\.\d{1,2})?$/.test(v),
    )
    .test(
      'max',
      `Amount must be ${max.toLocaleString('en-AU')} or less.`,
      (v) => !v || Number(v) <= max,
    );
  return required ? s.required(requiredMsg) : s.optional();
};

// Root form values from inside a nested array item (yup >= 1 exposes `from`).
type Ctx = yup.TestContext & { from?: { value: Record<string, any> }[] };
const rootOf = (ctx: yup.TestContext) => {
  const from = (ctx as Ctx).from;
  return from?.[from.length - 1]?.value ?? {};
};
const indexOf = (ctx: yup.TestContext) =>
  Number(/\[(\d+)\]/.exec(ctx.path)?.[1] ?? 0);

/** Array-level test: the current + previous entries reach back HISTORY_YEARS. */
const coversHistory = (currentStartKey: string, what: string) =>
  [
    'three-years',
    `Your ${what} history must cover the past ${HISTORY_YEARS} years. Please add a previous ${what}.`,
    function (this: yup.TestContext, list?: { startDate?: string | null }[]) {
      return (
        previousEntriesNeeded(this.parent[currentStartKey], list) <=
        (list?.length ?? 0)
      );
    },
  ] as const;

const startNotAfterEnd = (
  label = 'Start date must be on or before the end date.',
) =>
  function (this: yup.TestContext, start?: string | null) {
    const end = toDate(this.parent.endDate);
    const s = toDate(start);
    return (
      !s ||
      !end ||
      !s.isAfter(end, 'day') ||
      this.createError({ message: label })
    );
  };

export const factFindSchema = yup.object({
  // Step 1
  firstName: name(true),
  middleName: name(false),
  lastName: name(true),
  currentAddress: address(true),
  currentAddressStartDate: pastDate(true, 'Enter the date you moved in.'),
  previousAddresses: yup
    .array(
      yup.object({
        address: address(true),
        startDate: pastDate(true).test('order', startNotAfterEnd()),
        endDate: pastDate(true, 'Enter the date you moved out.').test(
          'before-next-address',
          '',
          function (end) {
            // Previous address N must end on or before the next-newer
            // address started (current address for N = 1).
            const i = indexOf(this);
            const root = rootOf(this);
            const nextStart =
              i === 0
                ? root.currentAddressStartDate
                : root.previousAddresses?.[i - 1]?.startDate;
            const e = toDate(end);
            const n = toDate(nextStart);
            if (!e || !n || !e.isAfter(n, 'day')) return true;
            const which = i === 0 ? 'current address' : `Previous Address ${i}`;
            return this.createError({
              message: `End date must be on or before the ${which} start date (${fmt(nextStart)}).`,
            });
          },
        ),
      }),
    )
    .max(MAX_PREVIOUS_ENTRIES)
    .test(...coversHistory('currentAddressStartDate', 'address'))
    .default([]),
  currentOccupation: shortText(true),
  currentEmployer: shortText(true),
  currentEmployerAddress: address(true),
  currentEmploymentStartDate: pastDate(true, 'Enter your start date.'),
  previousEmployments: yup
    .array(
      yup.object({
        occupation: shortText(true),
        employer: shortText(true),
        address: address(false),
        startDate: pastDate(true).test('order', startNotAfterEnd()),
        endDate: pastDate(false),
      }),
    )
    .max(MAX_PREVIOUS_ENTRIES)
    .test(...coversHistory('currentEmploymentStartDate', 'employment'))
    .default([]),

  // Step 2
  expenseCurrency: yup
    .string()
    .required(REQUIRED)
    .oneOf(
      currencies.map((c) => c.value),
      'Choose a currency from the list.',
    ),
  ...(Object.fromEntries(
    // Medical and Health is the one expense that must be answered (0 is fine,
    // with a reason); the rest count blank as 0.
    EXPENSE_KEYS.map((k) => [
      k,
      amount(k === 'medicalHealth', MAX_MONTHLY_EXPENSE),
    ]),
  ) as Record<ExpenseKey, yup.StringSchema>),
  medicalHealthZeroReason: longText(500).when('medicalHealth', {
    is: isExplicitZero,
    then: (s) => s.required(REQUIRED),
    otherwise: (s) => s.optional(),
  }),

  // Step 3
  ...(Object.fromEntries(
    ASSET_KEYS.map((k) => [
      k,
      amount(ASSET_FIELDS[k].required, MAX_ASSET_VALUE, REQUIRED),
    ]),
  ) as Record<AssetKey, yup.StringSchema>),
  otherAssets: longText(500).optional(),
  foreseeFinancialChanges: yup
    .string()
    .oneOf(['yes', 'no'], 'Please choose Yes or No.')
    .required('Please choose Yes or No.'),
  financialChangesDetail: longText(500).when('foreseeFinancialChanges', {
    is: 'yes',
    then: (s) => s.required(REQUIRED),
    otherwise: (s) => s.optional(),
  }),
  retirementAge: yup
    .string()
    .trim()
    .required(REQUIRED)
    .test(
      'age',
      'Enter a whole number between 18 and 100.',
      (v) =>
        !v ||
        (/^\d+$/.test(v.replace(/,/g, '')) &&
          Number(v.replace(/,/g, '')) >= 18 &&
          Number(v.replace(/,/g, '')) <= 100),
    ),
  declaration: yup
    .boolean()
    .oneOf([true], 'Please tick the declaration to submit your form.')
    .required('Please tick the declaration to submit your form.'),
});

export type FactFindFormType = yup.InferType<typeof factFindSchema>;

type FieldName = keyof FactFindFormType;

export const STEP_FIELDS: Record<number, FieldName[]> = {
  1: [
    'firstName',
    'middleName',
    'lastName',
    'currentAddress',
    'currentAddressStartDate',
    'previousAddresses',
    'currentOccupation',
    'currentEmployer',
    'currentEmployerAddress',
    'currentEmploymentStartDate',
    'previousEmployments',
  ],
  2: ['expenseCurrency', ...EXPENSE_KEYS, 'medicalHealthZeroReason'],
  3: [
    ...ASSET_KEYS,
    'otherAssets',
    'foreseeFinancialChanges',
    'financialChangesDetail',
    'retirementAge',
    'declaration',
  ],
};

/**
 * Starts a new application's fact-find from the client's last one. If the
 * purchase application names a different employer, that job becomes current
 * and the old current job moves to the top of the employment history.
 */
export const prefillFromPrevious = (
  previous: Partial<FactFindFormType>,
  income?: {
    occupation?: string;
    employer?: string;
    jobStartMonth?: string;
    jobStartYear?: string;
  },
): Partial<FactFindFormType> => {
  // The declaration is per submission, so they tick it again.
  const last: Partial<FactFindFormType> = { ...previous, declaration: false };
  const newEmployer = income?.employer?.trim() ?? '';
  const oldEmployer = String(last.currentEmployer ?? '').trim();
  if (!newEmployer || newEmployer.toLowerCase() === oldEmployer.toLowerCase())
    return last;
  const newStart = jobStartDate(income?.jobStartMonth, income?.jobStartYear);
  if (oldEmployer)
    last.previousEmployments = [
      {
        occupation: last.currentOccupation ?? '',
        employer: oldEmployer,
        address: last.currentEmployerAddress ?? EMPTY_ADDRESS,
        startDate: last.currentEmploymentStartDate ?? '',
        endDate: newStart,
      },
      ...(last.previousEmployments ?? []),
    ].slice(0, MAX_PREVIOUS_ENTRIES);
  last.currentOccupation = income?.occupation ?? '';
  last.currentEmployer = newEmployer;
  last.currentEmployerAddress = EMPTY_ADDRESS;
  last.currentEmploymentStartDate = newStart;
  return last;
};
